// Schnell-Bearbeitung der eigenen Kundenfelder aus dem Kalender-Rechtsklick.
// Zeigt nur Felder, die als "Termin-Menü" sichtbar & aktiv markiert sind.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, ListChecks } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomFieldsQuickDialog = ({ customerId, open, onOpenChange, onSaved }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [defsRes, valRes] = await Promise.all([
          axios.get(`${API}/settings/custom-fields`),
          axios.get(`${API}/customers/${customerId}/custom-fields`),
        ]);
        if (!active) return;
        setFields((defsRes.data?.fields || []).filter((f) => f.enabled && f.visibility?.appointment_menu));
        setValues(valRes.data?.values || {});
      } catch {
        toast.error('Felder konnten nicht geladen werden');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, customerId]);

  const setVal = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));
  const toggleMulti = (key, opt) => setValues((prev) => {
    const cur = Array.isArray(prev[key]) ? prev[key] : [];
    return { ...prev, [key]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/customers/${customerId}/custom-fields`, { values });
      toast.success('Eigene Felder gespeichert');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f) => {
    const v = values[f.key];
    switch (f.type) {
      case 'select':
        return (
          <Select value={v || '__none__'} onValueChange={(val) => setVal(f.key, val === '__none__' ? '' : val)}>
            <SelectTrigger data-testid={`qf-input-${f.key}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">– keine Angabe –</SelectItem>
              {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-2" data-testid={`qf-input-${f.key}`}>
            {(f.options || []).map((o) => {
              const active = Array.isArray(v) && v.includes(o);
              return (
                <button key={o} type="button" onClick={() => toggleMulti(f.key, o)}
                  className={`px-3 py-1 rounded-full border text-sm transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}>
                  {o}
                </button>
              );
            })}
          </div>
        );
      case 'textarea':
        return <textarea className="w-full min-h-[70px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`qf-input-${f.key}`} />;
      case 'number':
        return <Input type="number" value={v ?? ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`qf-input-${f.key}`} />;
      case 'date':
        return <Input type="date" value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`qf-input-${f.key}`} />;
      case 'boolean':
        return <Switch checked={!!v} onCheckedChange={(c) => setVal(f.key, c)} data-testid={`qf-input-${f.key}`} />;
      default:
        return <Input value={v || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`qf-input-${f.key}`} />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="custom-fields-quick-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5" /> Eigene Felder</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground p-4"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
        ) : !fields.length ? (
          <p className="text-sm text-muted-foreground p-2" data-testid="qf-empty">
            Keine Felder für das Termin-Menü aktiviert. Felder verwaltest du unter Einstellungen → Eigene Kundenfelder (Sichtbarkeit „Termin-Menü").
          </p>
        ) : (
          <div className="space-y-4">
            {fields.map((f) => (
              <div key={f.id} className="space-y-1.5">
                <Label className="text-sm font-medium flex items-center gap-2">
                  {f.label}
                  {f.unit ? <span className="text-xs text-muted-foreground">({f.unit})</span> : null}
                </Label>
                {renderInput(f)}
              </div>
            ))}
            <div className="flex justify-end pt-1">
              <Button onClick={save} disabled={saving} data-testid="qf-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Speichern
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomFieldsQuickDialog;
